/**
 * Editor da Arquitetura de Onboarding — a carta, os pilares, o roteiro, as
 * regras e o dicionário. Só a equipe edita; o cliente só lê (via link pessoal
 * gerado na página dele). Fonte: pedido de 26/jul.
 */
import { useEffect, useState } from "react";
import type { OnboardingContent } from "@unik/shared";
import { DEFAULT_ONBOARDING_CONTENT } from "@unik/shared";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { Input, TextArea } from "../../ui/Input";

function toParas(text: string): string[] {
  return text.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p !== "");
}
function fromParas(paras: string[]): string {
  return paras.join("\n\n");
}

interface PreviewLink {
  url: string;
}

export function OnboardingContentEditor() {
  const [content, setContent] = useState<OnboardingContent | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [preview, setPreview] = useState<PreviewLink | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewGenerating, setPreviewGenerating] = useState(false);
  const [previewCopied, setPreviewCopied] = useState(false);

  useEffect(() => {
    api
      .get<OnboardingContent>("/onboarding-content")
      .then(setContent)
      .catch(() => setContent(DEFAULT_ONBOARDING_CONTENT));
    api
      .get<PreviewLink | null>("/architecture-preview/current")
      .then(setPreview)
      .finally(() => setPreviewLoading(false));
  }, []);

  async function gerarPreview() {
    setPreviewGenerating(true);
    try {
      const res = await api.post<PreviewLink>("/architecture-preview/regenerate", {});
      setPreview(res);
      setPreviewCopied(false);
    } finally {
      setPreviewGenerating(false);
    }
  }

  async function regenerarPreview() {
    if (!window.confirm("Gerar um novo link de visualização? O link atual para de funcionar imediatamente.")) return;
    await gerarPreview();
  }

  async function copiarPreview() {
    if (preview === null) return;
    await navigator.clipboard.writeText(preview.url).catch(() => undefined);
    setPreviewCopied(true);
  }

  async function salvar() {
    if (content === null) return;
    setSaving(true);
    try {
      await api.put<OnboardingContent>("/onboarding-content", content);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  if (content === null) {
    return <AdminShell><p className="type-body text-text-secondary">Carregando…</p></AdminShell>;
  }

  function update<K extends keyof OnboardingContent>(key: K, value: OnboardingContent[K]) {
    setSaved(false);
    setContent((c) => (c === null ? c : { ...c, [key]: value }));
  }

  return (
    <AdminShell>
      <h1 className="type-h1 text-text-cream">Arquitetura de Onboarding</h1>
      <p className="type-body-sm mt-1 max-w-lg text-text-secondary">
        O roteiro que todo cliente novo recebe por um link pessoal, sem login. Edite aqui — o link
        que você já mandou atualiza sozinho, na hora.
      </p>

      <Card className="mt-6 max-w-md">
        <h2 className="type-h3 text-text-cream">Link de visualização</h2>
        <p className="type-body-sm mt-2 text-text-secondary">
          Um link único e permanente pra você conferir como o roteiro está ficando (ou mostrar pra
          qualquer pessoa) — sem estar atrelado a nenhum cliente. Atualiza sozinho a cada edição.
        </p>

        {previewLoading && <p className="type-caption mt-4 text-text-tertiary">Carregando…</p>}

        {!previewLoading && preview === null && (
          <div className="mt-4">
            <Button onClick={gerarPreview} loading={previewGenerating}>Gerar link de visualização</Button>
          </div>
        )}

        {preview !== null && (
          <div className="mt-4 rounded-[var(--radius-control)] border border-border-subtle p-4">
            <p className="type-caption break-all text-text-secondary">{preview.url}</p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Button size="sm" onClick={copiarPreview}>{previewCopied ? "Copiado!" : "Copiar link"}</Button>
              <a href={preview.url} target="_blank" rel="noreferrer">
                <Button size="sm" variant="secondary">Abrir</Button>
              </a>
              <Button size="sm" variant="ghost" onClick={regenerarPreview} loading={previewGenerating}>
                Regenerar link
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Carta da diretora */}
      <Card className="mt-6">
        <h2 className="type-h3 text-text-cream">{content.carta.title}</h2>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            label="Título da página" value={content.carta.title}
            onChange={(e) => update("carta", { ...content.carta, title: e.target.value })}
          />
          <TextArea
            label="Texto (separe os parágrafos com uma linha em branco)" rows={16}
            value={fromParas(content.carta.paragraphs)}
            onChange={(e) => update("carta", { ...content.carta, paragraphs: toParas(e.target.value) })}
          />
          <Input
            label="Assinatura" value={content.carta.signature}
            onChange={(e) => update("carta", { ...content.carta, signature: e.target.value })}
          />
        </div>
      </Card>

      {/* O jeito Unik */}
      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">O jeito Unik</h2>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            label="Título" value={content.jeitoUnik.title}
            onChange={(e) => update("jeitoUnik", { ...content.jeitoUnik, title: e.target.value })}
          />
          <TextArea
            label="Introdução" rows={2} value={content.jeitoUnik.intro}
            onChange={(e) => update("jeitoUnik", { ...content.jeitoUnik, intro: e.target.value })}
          />
          {content.jeitoUnik.pilares.map((p, i) => (
            <div key={i} className="rounded-[var(--radius-control)] border border-border-subtle p-3">
              <Input
                label={`Pilar ${i + 1} — título`} value={p.title}
                onChange={(e) => {
                  const pilares = [...content.jeitoUnik.pilares];
                  pilares[i] = { ...p, title: e.target.value };
                  update("jeitoUnik", { ...content.jeitoUnik, pilares });
                }}
              />
              <div className="mt-3">
                <TextArea
                  label="Texto" rows={4} value={p.body}
                  onChange={(e) => {
                    const pilares = [...content.jeitoUnik.pilares];
                    pilares[i] = { ...p, body: e.target.value };
                    update("jeitoUnik", { ...content.jeitoUnik, pilares });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* O roteiro */}
      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">O roteiro</h2>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            label="Título" value={content.roteiro.title}
            onChange={(e) => update("roteiro", { ...content.roteiro, title: e.target.value })}
          />
          <TextArea
            label="Introdução" rows={2} value={content.roteiro.intro}
            onChange={(e) => update("roteiro", { ...content.roteiro, intro: e.target.value })}
          />
          {content.roteiro.passos.map((s, i) => (
            <div key={i} className="rounded-[var(--radius-control)] border border-border-subtle p-3">
              <p className="type-overline text-gold">Cena {s.number}</p>
              <div className="mt-2">
                <Input
                  label="Título" value={s.title}
                  onChange={(e) => {
                    const passos = [...content.roteiro.passos];
                    passos[i] = { ...s, title: e.target.value };
                    update("roteiro", { ...content.roteiro, passos });
                  }}
                />
              </div>
              <div className="mt-3">
                <TextArea
                  label="Texto" rows={3} value={s.body}
                  onChange={(e) => {
                    const passos = [...content.roteiro.passos];
                    passos[i] = { ...s, body: e.target.value };
                    update("roteiro", { ...content.roteiro, passos });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* As regras do set */}
      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">As regras do set</h2>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            label="Título" value={content.regras.title}
            onChange={(e) => update("regras", { ...content.regras, title: e.target.value })}
          />
          <TextArea
            label="Introdução" rows={2} value={content.regras.intro}
            onChange={(e) => update("regras", { ...content.regras, intro: e.target.value })}
          />
          {content.regras.itens.map((it, i) => (
            <div key={i} className="rounded-[var(--radius-control)] border border-border-subtle p-3">
              <Input
                label="Regra" value={it.label}
                onChange={(e) => {
                  const itens = [...content.regras.itens];
                  itens[i] = { ...it, label: e.target.value };
                  update("regras", { ...content.regras, itens });
                }}
              />
              <div className="mt-3">
                <TextArea
                  label="Texto" rows={2} value={it.body}
                  onChange={(e) => {
                    const itens = [...content.regras.itens];
                    itens[i] = { ...it, body: e.target.value };
                    update("regras", { ...content.regras, itens });
                  }}
                />
              </div>
            </div>
          ))}

          <Input
            label="Título — o que eu preciso de você" value={content.regras.precisoDeVoceTitle}
            onChange={(e) => update("regras", { ...content.regras, precisoDeVoceTitle: e.target.value })}
          />
          <TextArea
            label="Introdução" rows={2} value={content.regras.precisoDeVoceIntro}
            onChange={(e) => update("regras", { ...content.regras, precisoDeVoceIntro: e.target.value })}
          />
          {content.regras.precisoDeVoceItens.map((it, i) => (
            <div key={i} className="rounded-[var(--radius-control)] border border-border-subtle p-3">
              <Input
                label="Item" value={it.label}
                onChange={(e) => {
                  const itens = [...content.regras.precisoDeVoceItens];
                  itens[i] = { ...it, label: e.target.value };
                  update("regras", { ...content.regras, precisoDeVoceItens: itens });
                }}
              />
              <div className="mt-3">
                <TextArea
                  label="Texto" rows={2} value={it.body}
                  onChange={(e) => {
                    const itens = [...content.regras.precisoDeVoceItens];
                    itens[i] = { ...it, body: e.target.value };
                    update("regras", { ...content.regras, precisoDeVoceItens: itens });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Dicionário */}
      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">Dicionário Unik</h2>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            label="Título" value={content.dicionario.title}
            onChange={(e) => update("dicionario", { ...content.dicionario, title: e.target.value })}
          />
          <TextArea
            label="Introdução" rows={2} value={content.dicionario.intro}
            onChange={(e) => update("dicionario", { ...content.dicionario, intro: e.target.value })}
          />
          {content.dicionario.termos.map((t, i) => (
            <div key={i} className="rounded-[var(--radius-control)] border border-border-subtle p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <Input
                    label="Termo" value={t.term}
                    onChange={(e) => {
                      const termos = [...content.dicionario.termos];
                      termos[i] = { ...t, term: e.target.value };
                      update("dicionario", { ...content.dicionario, termos });
                    }}
                  />
                </div>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => {
                    const termos = content.dicionario.termos.filter((_, j) => j !== i);
                    update("dicionario", { ...content.dicionario, termos });
                  }}
                >
                  Remover
                </Button>
              </div>
              <div className="mt-3">
                <TextArea
                  label="Definição" rows={2} value={t.definition}
                  onChange={(e) => {
                    const termos = [...content.dicionario.termos];
                    termos[i] = { ...t, definition: e.target.value };
                    update("dicionario", { ...content.dicionario, termos });
                  }}
                />
              </div>
            </div>
          ))}
          <Button
            size="sm" variant="secondary"
            onClick={() =>
              update("dicionario", {
                ...content.dicionario,
                termos: [...content.dicionario.termos, { term: "", definition: "" }],
              })
            }
          >
            + Adicionar termo
          </Button>
        </div>
      </Card>

      {/* Encerramento */}
      <Card className="mt-4">
        <h2 className="type-h3 text-text-cream">Encerramento</h2>
        <div className="mt-4 flex flex-col gap-4">
          <Input
            label="Título" value={content.encerramento.title}
            onChange={(e) => update("encerramento", { ...content.encerramento, title: e.target.value })}
          />
          <TextArea
            label="Texto (separe os parágrafos com uma linha em branco)" rows={5}
            value={fromParas(content.encerramento.paragraphs)}
            onChange={(e) => update("encerramento", { ...content.encerramento, paragraphs: toParas(e.target.value) })}
          />
          <Input
            label="Assinatura" value={content.encerramento.signature}
            onChange={(e) => update("encerramento", { ...content.encerramento, signature: e.target.value })}
          />
          <Input
            label="Perfis (separados por espaço)" value={content.encerramento.handles.join(" ")}
            onChange={(e) => update("encerramento", { ...content.encerramento, handles: e.target.value.split(/\s+/).filter(Boolean) })}
          />
        </div>
      </Card>

      <div className="sticky bottom-4 mt-6 flex items-center gap-3 rounded-[var(--radius-control)] bg-surface-overlay p-3 shadow-[var(--shadow-lg)]">
        <Button onClick={salvar} loading={saving}>Salvar alterações</Button>
        {saved && <span className="type-caption text-success">Salvo!</span>}
      </div>
    </AdminShell>
  );
}
